import Cta from "@/components/Cta";
import { isFilled } from "@prismicio/client";
import { PrismicImage, PrismicLink, PrismicRichText } from "@prismicio/react";
import Vague from "@/components/Vague";


/**
 * @typedef {import("@prismicio/client").Content.HeroSlice} HeroSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<HeroSlice>} HeroProps
 * @type {import("react").FC<HeroProps>}
 */
const Hero = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`${slice.variation === "default" ? "lg:flex-row bg-bleu-950 sticky -top-130 lg:top-0 z-0 lg:h-[99dvh]" : "lg:flex-row-reverse bg-terracota-400"} flex flex-col items-stretch text-white`}
    >
      <div className="w-full lg:w-1/2 relative lg:min-h-[99dvh]">
        {isFilled.image(slice.primary.picture) && (
          <PrismicImage
            field={{
              ...slice.primary.picture,
              alt: slice.primary.picture.alt || "Image d'illustration",
            }}
            className="object-cover w-full lg:h-full h-130"
          />
        )}
      </div>
      <div className="w-full lg:w-1/2 px-6 pt-10 pb-24 lg:pl-12 lg:pr-30  lg:pt-20 lg:pb-34">
        <div className="flex flex-col gap-6 lg:gap-10 lg:max-w-150">
          <Vague color={slice.variation === "default" ? "#D96545" : "#EFF7FB"} />

          {isFilled.richText(slice.primary.title) && (
            <PrismicRichText
              field={slice.primary.title}
              components={{
                heading1: ({ children }) => <h1 className="h1">{children}</h1>,
                heading2: ({ children }) => <h2 className="h1">{children}</h2>,
              }}
            />
          )}

          {isFilled.richText(slice.primary.text) && (
            <div className="flex flex-col gap-2">
            <PrismicRichText
              field={slice.primary.text}
              components={{
                paragraph: ({ children }) => <p className={`${slice.variation === "default" ? "body-l" : "body-s"}`}>{children}</p>,
                strong: ({ children }) => <strong>{children}</strong>,
                em: ({ children }) => <em>{children}</em>,
                hyperlink: ({ children, node }) => (
                  <PrismicLink field={node.data}>{children}</PrismicLink>
                ),
                list: ({ children }) => <ul>{children}</ul>,
                listItem: ({ children }) => <li>{children}</li>,
                oList: ({ children }) => <ol>{children}</ol>,
                oListItem: ({ children }) => <li>{children}</li>,
              }}
            />
            </div>
          )}

          {isFilled.keyText(slice.primary.address) && (
            <div className="flex gap-4 md:gap-2 md:items-center">
              <svg width="14" height="20" viewBox="0 0 14 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-7 h-7">
                <path d="M6.575 14.6C8.35833 13.1667 9.70833 11.7917 10.625 10.475C11.5417 9.15833 12 7.90833 12 6.725C12 5.70833 11.8167 4.84583 11.45 4.1375C11.0833 3.42917 10.625 2.85 10.075 2.4C9.525 1.95 8.93333 1.625 8.3 1.425C7.66667 1.225 7.09167 1.125 6.575 1.125C6.05833 1.125 5.48333 1.225 4.85 1.425C4.21667 1.625 3.625 1.95 3.075 2.4C2.525 2.85 2.06667 3.42917 1.7 4.1375C1.33333 4.84583 1.15 5.70833 1.15 6.725C1.15 7.90833 1.60833 9.15833 2.525 10.475C3.44167 11.7917 4.79167 13.1667 6.575 14.6ZM6.575 15.775C6.475 15.775 6.375 15.7667 6.275 15.75C6.175 15.7333 6.09167 15.6917 6.025 15.625C3.99167 14.025 2.47917 12.4958 1.4875 11.0375C0.495833 9.57917 0 8.14167 0 6.725C0 5.60833 0.2 4.63333 0.6 3.8C1 2.96667 1.5125 2.26667 2.1375 1.7C2.7625 1.13333 3.46667 0.708333 4.25 0.425C5.03333 0.141667 5.80833 0 6.575 0C7.34167 0 8.11667 0.141667 8.9 0.425C9.68333 0.708333 10.3875 1.13333 11.0125 1.7C11.6375 2.26667 12.15 2.96667 12.55 3.8C12.95 4.63333 13.15 5.60833 13.15 6.725C13.15 8.14167 12.6542 9.57917 11.6625 11.0375C10.6708 12.4958 9.15833 14.025 7.125 15.625C7.05833 15.6917 6.975 15.7333 6.875 15.75C6.775 15.7667 6.675 15.775 6.575 15.775ZM6.575 8.125C7.00833 8.125 7.37917 7.97083 7.6875 7.6625C7.99583 7.35417 8.15 6.98333 8.15 6.55C8.15 6.11667 7.99583 5.75 7.6875 5.45C7.37917 5.15 7.00833 5 6.575 5C6.14167 5 5.77083 5.15417 5.4625 5.4625C5.15417 5.77083 5 6.14167 5 6.575C5 7.00833 5.15417 7.375 5.4625 7.675C5.77083 7.975 6.14167 8.125 6.575 8.125ZM0.575 19.5C0.408333 19.5 0.270833 19.4458 0.1625 19.3375C0.0541666 19.2292 0 19.0917 0 18.925C0 18.7583 0.0541666 18.625 0.1625 18.525C0.270833 18.425 0.408333 18.375 0.575 18.375H12.575C12.7417 18.375 12.8792 18.4292 12.9875 18.5375C13.0958 18.6458 13.15 18.7833 13.15 18.95C13.15 19.1 13.0958 19.2292 12.9875 19.3375C12.8792 19.4458 12.7417 19.5 12.575 19.5H0.575Z" fill="#EFF7FB" />
              </svg>
              <address className="body-m not-italic">{slice.primary.address}</address>
            </div>
          )}

          {isFilled.link(slice.primary.cta) && (
            <Cta color="terracota-400" field={slice.primary.cta} />
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
